import XInput from './XInput';
import XSelect from './XSelect';
import Constants from '../../Constants';
import useAddressSuggestions from '../../hooks/useAddressSuggestions';

const fieldsetClasses = 'border-0 p-0 m-0';

const legendClasses = 'font-semibold mb-4';

const gridClasses = 'grid grid-cols-12 gap-4 items-start';

const colHalfClasses = 'md:col-span-6 col-span-12';

const labelClasses = 'block mb-2 text-content font-sans';

const labelSpinnerClasses = `inline-block h-3 w-3 ml-2
  animate-spin rounded-full border-2
  border-primary border-t-transparent align-middle`;

const inputBaseClasses = `w-full h-10 px-3 py-1.5
  font-sans text-base text-content
  bg-white border border-border rounded-md transition-colors
  focus:outline-none focus:ring-2 focus:ring-primary-ring focus:border-primary`;

const inputAnchorClasses = 'relative';

const errorClasses = 'block mt-1 ml-1 text-sm text-danger';

const suggestionsListClasses = `absolute top-full left-0 w-full
  z-50 mt-1 bg-surface
  border border-border-light rounded-md shadow overflow-hidden
  list-none p-0 m-0`;

const suggestionItemClasses = `block px-3 py-2 cursor-pointer
  hover:bg-surface-alt transition-colors`;

const suggestionPrimaryClasses = 'font-medium text-sm';

const suggestionSecondaryClasses = 'text-body text-xs';

const AddressForm = ({ fieldPath, onAfterChange, legend = 'Address', formik }) => {
    const {
        fields,
        zipCodeLoading,
        isSearchingStreet,
        zipError,
        streetSuggestions,
        onZipChange,
        onStreetChange,
        onSuggestionSelect,
        clearSuggestions,
        onFieldChange,
    } = useAddressSuggestions({ fieldPath, onAfterChange, formik });

    return (
        <fieldset className={fieldsetClasses}>
            {legend && <legend className={legendClasses}>{legend}</legend>}
            <div className={gridClasses}>
                <div className={colHalfClasses}>
                    <label className={labelClasses}>
                        Street Address
                        {isSearchingStreet && (
                            <span
                                className={labelSpinnerClasses}
                                role="status"
                                aria-label="Searching addresses"
                            />
                        )}
                    </label>
                    <div className={inputAnchorClasses}>
                        <input
                            type="text"
                            name="address"
                            value={fields.address}
                            onChange={onStreetChange}
                            onBlur={() => setTimeout(clearSuggestions, 150)}
                            placeholder="123 Main St"
                            autoComplete="off"
                            className={inputBaseClasses}
                        />
                        {streetSuggestions.length > 0 && (
                            <ul
                                className={suggestionsListClasses}
                                data-testid="street-suggestions"
                            >
                                {streetSuggestions.map((s, i) => (
                                    <li
                                        key={i}
                                        className={suggestionItemClasses}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            onSuggestionSelect(s);
                                        }}
                                    >
                                        <div className={suggestionPrimaryClasses}>{s.street || s.label}</div>
                                        <div className={suggestionSecondaryClasses}>
                                            {[s.city, s.state, s.zip].filter(Boolean).join(', ')}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className={colHalfClasses}>
                    <label className={labelClasses}>
                        Zip Code
                        {zipCodeLoading && (
                            <span
                                className={labelSpinnerClasses}
                                role="status"
                                aria-label="Looking up zip code"
                            />
                        )}
                    </label>
                    <input
                        type="text"
                        name="zip"
                        value={fields.zip}
                        onChange={onZipChange}
                        placeholder="12345"
                        maxLength={5}
                        inputMode="numeric"
                        className={inputBaseClasses}
                        aria-describedby={zipError ? 'zip-error' : undefined}
                    />
                    {zipError && (
                        <span id="zip-error" className={errorClasses}>{zipError}</span>
                    )}
                </div>

                <div className={colHalfClasses}>
                    <XSelect
                        label="State"
                        name="state"
                        value={fields.state}
                        onChange={onFieldChange('state')}
                        options={Constants.Dropdowns.States}
                        emptyOption={true}
                        disabled={zipCodeLoading}
                    />
                </div>

                <div className={colHalfClasses}>
                    <XInput
                        label="City"
                        name="city"
                        value={fields.city}
                        onChange={onFieldChange('city')}
                        placeholder={zipCodeLoading ? 'Looking up…' : 'City'}
                        disabled={zipCodeLoading}
                    />
                </div>
            </div>
        </fieldset>
    );
};

export default AddressForm;